// Live "this will reach N people" counter for a rule set or saved audience.
import React, { useEffect, useRef, useState } from "react";
import { Users, AlertCircle } from "lucide-react";
import { useBroadcastPage } from "../context";
import { broadcastApi } from "../api";

const AudienceReach = ({ rules, audienceId, ready, onCount }) => {
  const { s, t, language } = useBroadcastPage();
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const reqId = useRef(0);
  const key = JSON.stringify({ rules, audienceId });

  useEffect(() => {
    if (!ready) {
      setState({ loading: false, data: null, error: null });
      onCount?.(null);
      return undefined;
    }
    const id = ++reqId.current;
    setState((p) => ({ ...p, loading: true, error: null }));
    const timer = setTimeout(async () => {
      try {
        const body = rules && rules.length ? { rules } : { audienceId };
        const data = await broadcastApi.previewAudience(body);
        if (id !== reqId.current) return;
        setState({ loading: false, data, error: null });
        onCount?.(data?.count ?? null);
      } catch (e) {
        if (id !== reqId.current) return;
        setState({ loading: false, data: null, error: e.message });
        onCount?.(null);
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ready]);

  if (!ready) {
    return (
      <div style={{ ...s.reachBox, color: s.colors.muted, fontSize: 13.5 }}>
        <Users size={18} /> {t.reach.incomplete}
      </div>
    );
  }
  if (state.error) {
    return (
      <div style={{ ...s.notice("warning"), marginTop: 14 }}>
        <AlertCircle size={16} style={{ flexShrink: 0 }} /> {state.error}
      </div>
    );
  }

  const d = state.data;
  const sampleName = (x) => (language === "ar" && x.nameAr) || x.name || x.email;
  return (
    <div style={s.reachBox} aria-live="polite">
      <Users size={20} color="#2e7cba" style={{ flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: s.colors.text }}>
          {state.loading || !d ? t.reach.counting : t.reach.reaches(d.count)}
        </div>
        {d && !state.loading && d.count > 0 && (
          <div style={{ fontSize: 12.5, color: s.colors.muted, marginTop: 2, lineHeight: 1.6 }}>
            {Object.entries(d.byRole)
              .map(([role, n]) => `${n} ${t.roles[role] || role}`)
              .join(" · ")}
            {d.sample?.length ? ` — ${t.reach.eg} ${d.sample.slice(0, 3).map(sampleName).filter(Boolean).join(t.rules.listSep)}` : ""}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudienceReach;
